/**
 * House number format — the single money/number vocabulary for the platform.
 *
 * Rules (from the Reppr Elite handoff, adopted platform-wide):
 *  - One decimal above $1M:   $44.1M
 *  - Whole thousands below:   $450K
 *  - Em dash for nothing:     null / undefined / NaN / 0 → —
 *  - `exact` only where money literally changes hands (an invoice line,
 *    a payment, an athlete statement): $1,234,567
 *
 * Do not re-implement these per screen — the pre-2026 inconsistency was
 * caused entirely by every screen formatting its own figures.
 */

export const EM_DASH = '—'

/** Compact money in the house format. `exact` renders the full figure. */
export function money(v: number | null | undefined, exact = false): string {
  if (v == null || !Number.isFinite(v) || v === 0) return EM_DASH
  const sign = v < 0 ? '−' : ''
  const abs = Math.abs(v)
  if (exact) return `${sign}$${abs.toLocaleString('en-US')}`
  if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(1)}B`
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(1)}M`
  if (abs >= 1e3) return `${sign}$${Math.round(abs / 1e3)}K`
  return `${sign}$${Math.round(abs)}`
}

/** Signed change: +$1.2M for gains, −$450K for losses, — for nothing. */
export function delta(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v) || v === 0) return EM_DASH
  return v > 0 ? `+${money(v)}` : money(v)
}

/** Percentage with optional decimals: 3%, 2.5%. — when not finite. */
export function pct(v: number | null | undefined, dp = 0): string {
  return v != null && Number.isFinite(v) ? `${v.toFixed(dp)}%` : EM_DASH
}

/** Day count: 45d. — when not finite. */
export function days(v: number | null | undefined): string {
  return v != null && Number.isFinite(v) ? `${v}d` : EM_DASH
}

/**
 * Chart axis labels. A baseline is the origin of a scale — neither "nothing"
 * (—) nor "we computed zero" ($0) — so it gets its own formatter rather than
 * reusing money(). Whole millions drop the trailing .0, which is noise on an
 * axis where every label is a round number.
 */
export function axis(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return ''
  if (v === 0) return '$0'
  const sign = v < 0 ? '−' : ''
  const abs = Math.abs(v)
  if (abs >= 1e9) {
    const b = abs / 1e9
    return `${sign}$${Number.isInteger(b) ? b : b.toFixed(1)}B`
  }
  if (abs >= 1e6) {
    const m = abs / 1e6
    return `${sign}$${Number.isInteger(m) ? m : m.toFixed(1)}M`
  }
  if (abs >= 1e3) return `${sign}$${Math.round(abs / 1e3)}K`
  return `${sign}$${Math.round(abs)}`
}
